import React from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import Tool, { type BaseTool, type ToolButtonComponent } from './Tool';
import tools from '../tools';
import ToolButton from '../toolbars/ToolButton';
import styles from '../styles.module.css';
import WhiteboardController from '../controllers/WhiteboardController';
import {
  hasCommentableBehaviors,
  hasLockBehaviors,
  hasSelectedTableCell,
} from '../objects/guards';
import ToggleMenuItem from '../components/ToggleMenuItem';
import {
  getApplicableSelectedObjects,
  type ToolCreateRecords,
} from './ToolCreateRecords';
import useElementRefresh from '../components/hooks/useElementRefresh';
import type Table from '../objects/Table';
import classNames from 'classnames';
import ToolImportHierarchy from './ToolImportHierarchy';

const CONTEXT_MENU_OBJECT = 'object';
const CONTEXT_MENU_CANVAS = 'canvas';
const CONTEXT_MENU_TABLE = 'table';

export type ContextMenuType =
  | typeof CONTEXT_MENU_OBJECT
  | typeof CONTEXT_MENU_CANVAS
  | typeof CONTEXT_MENU_TABLE;

interface ToolMenuItemProps {
  controller: WhiteboardController;
  selection?: fabric.Object | null;
  tool: typeof BaseTool;
  params?: Record<string, unknown>; // TODO use a per-tool generic for this
  type?: 'danger' | 'warning' | 'success' | 'info';
  icon?: string;
  comingSoon?: boolean;
  children?: React.ReactNode;
  menuItemNameClassName?: string;
}

const ToolMenuItem: React.FC<ToolMenuItemProps> = ApplicationView(
  ({
    controller,
    selection,
    tool,
    params,
    type,
    icon,
    comingSoon,
    children,
    menuItemNameClassName,
  }: ToolMenuItemProps): React.ReactElement | null => {
    if (!tool.available({ controller, selection })) return null;

    return (
      <aha-menu-item
        type={type}
        key={tool.toolName}
        {...(!controller.isToolEnabled({ tool }) && {
          disabled: true,
        })}
        onClick={() =>
          controller.actionApplyTool(
            tool,
            params,
            tool.shouldRefocusWhiteboardWhenApplied
          )
        }
        data-testid={`More-menu-${tool.testid()}`}
      >
        <a className={styles.menuItemWithShortcut}>
          <div className={classNames(menuItemNameClassName)}>
            {icon && (
              <>
                {' '}
                <aha-icon icon={icon} />{' '}
              </>
            )}
            {children || tool.toolName}
          </div>
          {comingSoon && (
            <span className={styles.menuItemComingSoon}>COMING SOON!</span>
          )}
          {tool.shortcut?.pretty && tool.shortcut.pretty.length > 0 && (
            <>
              {' '}
              <span className={styles.menuItemShortcut}>
                {tool.shortcut.pretty}
              </span>
            </>
          )}
        </a>
      </aha-menu-item>
    );
  }
);

const CanvasMenuItems = ({
  controller,
  selection,
  showShapeAuthors,
  showBackgroundGrid,
}: {
  controller: WhiteboardController;
  selection?: fabric.Object;
  showShapeAuthors: boolean;
  showBackgroundGrid: boolean;
}) => {
  const { DebugMenu } = controller.props.debug ?? {};
  const shapeItems = [
    tools.ToolStylusText,
    tools.ToolStylusShape,
    tools.ToolStylusNote,
    tools.ToolStylusImage,
    tools.ToolStylusFrame,
    tools.ToolStylusComment,
  ].filter(tool => tool.available({ controller, selection }));

  return (
    <>
      <aha-menu-group label='Actions'>
        <ToolMenuItem
          tool={tools.ToolPaste}
          controller={controller}
          selection={selection}
        />
        <ToolMenuItem
          tool={tools.ToolCopyViewportUrl}
          controller={controller}
          selection={selection}
        />
      </aha-menu-group>
      <aha-menu-group>
        <ToggleMenuItem
          value={showBackgroundGrid}
          setValue={updated => {
            controller.actionToggleBackgroundGrid(updated);
          }}
        >
          Show grid
        </ToggleMenuItem>

        <ToggleMenuItem
          value={showShapeAuthors}
          setValue={updated => {
            controller.actionToggleShowShapeAuthors(updated);
          }}
        >
          Show note author
        </ToggleMenuItem>
      </aha-menu-group>

      {tools.ToolAddDependencyLines.available({
        controller,
      }) && (
        <aha-menu-group>
          <ToolMenuItem
            tool={tools.ToolAddDependencyLines}
            controller={controller}
            selection={selection}
          >
            Show dependency lines
          </ToolMenuItem>
        </aha-menu-group>
      )}
      {tools.ToolRemoveDependencyLines.available({
        controller,
      }) && (
        <aha-menu-group>
          <ToolMenuItem
            tool={tools.ToolRefreshDependencyLines}
            controller={controller}
            selection={selection}
          >
            Refresh dependency lines
          </ToolMenuItem>
          <ToolMenuItem
            tool={tools.ToolRemoveDependencyLines}
            controller={controller}
            selection={selection}
          >
            Remove dependency lines
          </ToolMenuItem>
        </aha-menu-group>
      )}
      <aha-menu-group label='Insert'>
        {shapeItems.map((item, index) => (
          <ToolMenuItem
            key={index}
            tool={item}
            controller={controller}
            selection={selection}
          />
        ))}
      </aha-menu-group>
      <aha-menu-group label='Export'>
        <ToolMenuItem
          tool={tools.ToolExportSVG}
          controller={controller}
          selection={selection}
        />
        <ToolMenuItem
          tool={tools.ToolExportPNG}
          controller={controller}
          selection={selection}
        />
      </aha-menu-group>
      {DebugMenu && <DebugMenu controller={controller} />}
    </>
  );
};

interface CreateRecordsInfo {
  applicableSelection: fabric.Object[];
  availableRecordTools: (typeof ToolCreateRecords)[];
}

const objectMenuAvailability = (
  controller: WhiteboardController,
  selection?: fabric.Object | null
) => {
  const tableCellSelected = selection && hasSelectedTableCell(selection);

  const commenting =
    selection &&
    tools.ToolComment.available({ controller, selection }) &&
    hasCommentableBehaviors(selection);

  const lockOrUnlock =
    selection &&
    !tableCellSelected &&
    hasLockBehaviors(selection) &&
    (tools.ToolLock.available({ controller, selection }) ||
      tools.ToolUnlock.available({ controller, selection }));

  const unlockAll = tools.ToolUnlockAll.available({ controller });

  // Check if copy/paste style actions are available
  const styleCopyPaste =
    selection &&
    (tools.ToolCopyStyle.available({ controller, selection }) ||
      tools.ToolPasteStyle.available({ controller, selection }));

  const applicableSelection =
    selection && getApplicableSelectedObjects(selection);
  const availableRecordTools = [
    tools.ToolCreateIdeas,
    tools.ToolCreateFeatures,
    tools.ToolCreateRequirements,
    tools.ToolCreateEpics,
    tools.ToolCreateReleases,
    tools.ToolCreateInitiatives,
    tools.ToolCreateGoals,
    tools.ToolCreatePdlcSteps,
    ...controller.integrationsController.getCreateIntegrationRecordTools(),
  ].filter(tool => tool.available({ controller, selection }));

  const createRecordsInfo =
    applicableSelection?.length &&
    availableRecordTools.length &&
    controller.allowRecordLinking()
      ? {
          applicableSelection,
          availableRecordTools,
        }
      : undefined;

  return {
    commenting,
    lockOrUnlock,
    unlockAll,
    primaryActions: !tableCellSelected,
    createRecordsInfo,
    styleToolsOnly: styleCopyPaste && tableCellSelected,
    deletion: !tableCellSelected,
  };
};

const ConvertToMenu: React.FC<{
  createRecordsInfo: CreateRecordsInfo;
  controller: WhiteboardController;
  selection?: fabric.Object | null;
}> = ({
  createRecordsInfo: { applicableSelection, availableRecordTools },
  controller,
  selection,
}) => {
  const isMultiSelection = applicableSelection.length > 1;
  const groups: Record<string, typeof availableRecordTools> = {};
  for (const tool of availableRecordTools) {
    const { category } = tool;
    groups[category] ||= [];
    groups[category].push(tool);
  }
  return (
    <aha-menu-item>
      <aha-menu>
        <div slot='control'>Convert to</div>
        <aha-menu-content>
          {Object.entries(groups).map(([label, tools]) => (
            <aha-menu-group key={label} label={label}>
              {tools.map(recordTool => (
                <ToolMenuItem
                  key={recordTool.toolName}
                  tool={recordTool}
                  controller={controller}
                  selection={selection}
                >
                  {recordTool.description(isMultiSelection)}
                </ToolMenuItem>
              ))}
            </aha-menu-group>
          ))}
        </aha-menu-content>
      </aha-menu>
    </aha-menu-item>
  );
};

const importRecordMenu = (controller: WhiteboardController) => {
  const isStandaloneProduct =
    window.currentAccount?.onlyKnowledgeFlavor ||
    window.currentAccount?.onlyWhiteboardsFlavor;

  return (
    <aha-menu-item>
      <aha-menu hover-behavior='hover'>
        <div slot='control'>
          <span className={styles.menuItemWithShortcut}>
            <div>Import related records</div>
          </span>
        </div>
        <aha-menu-content>
          {!isStandaloneProduct && (
            <>
              <aha-menu-item
                onClick={() =>
                  controller.actionApplyTool(
                    tools.ToolImportHierarchy,
                    { relationshipType: 'parents' },
                    true
                  )
                }
              >
                <a>Parents</a>
              </aha-menu-item>
              <aha-menu-item
                onClick={() =>
                  controller.actionApplyTool(
                    tools.ToolImportHierarchy,
                    { relationshipType: 'children' },
                    true
                  )
                }
              >
                <a>Children</a>
              </aha-menu-item>
              <aha-menu-item
                onClick={() =>
                  controller.actionApplyTool(
                    tools.ToolImportHierarchy,
                    { relationshipType: 'dependencies' },
                    true
                  )
                }
              >
                <a>Dependencies</a>
              </aha-menu-item>
            </>
          )}
          <aha-menu-item
            onClick={() =>
              controller.actionApplyTool(
                tools.ToolImportHierarchy,
                { relationshipType: 'related' },
                true
              )
            }
          >
            <a>Related</a>
          </aha-menu-item>
        </aha-menu-content>
      </aha-menu>
    </aha-menu-item>
  );
};

const ObjectMenuItems: React.FC<{
  controller: WhiteboardController;
  selection?: fabric.Object | null;
}> = ({ controller, selection }) => {
  const availability = objectMenuAvailability(controller, selection);

  let customShapesMenuItem: JSX.Element | undefined;
  if (window.currentAccount?.whiteboardsAdvanced) {
    customShapesMenuItem = (
      <ToolMenuItem
        tool={tools.ToolCreateCustomShape}
        controller={controller}
        selection={selection}
      />
    );
  } else if (window.currentAccount?.hasCrossProductPromotionsEnabled) {
    const upgradeLocation = 'whiteboards_custom_shapes_link';
    customShapesMenuItem = (
      <aha-menu-item>
        <a
          className={styles.menuItemWithShortcut}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
          onClick={() => {
            window.open(
              window.location.origin +
                `/suite/whiteboards?upgrade_location=${upgradeLocation}#streamline_diagram_creation`,
              '_blank'
            );
            window.AhaAnalytics?.track(
              'Feature-7476560556666826428',
              'User clicks promotion',
              {
                'Upgrade location': upgradeLocation,
                'Promotion product': 'Whiteboards',
                'Originating product': window.currentAccount?.primaryFlavor,
              }
            );
          }}
        >
          <span style={{ opacity: 0.3 }}>Save as custom shape</span>
          <span>
            <aha-promo-try-pill style={{ cursor: 'pointer' }} />
            <aha-promo-view-pixel
              upgrade-location={upgradeLocation}
            ></aha-promo-view-pixel>{' '}
          </span>
        </a>
      </aha-menu-item>
    );
  }

  const importRecordsMenuItem =
    selection && ToolImportHierarchy.available({ selection, controller });

  return (
    <>
      {availability.primaryActions && (
        <>
          <aha-menu-group label='Actions'>
            <ToolMenuItem
              tool={tools.ToolCut}
              controller={controller}
              selection={selection}
            />
            <ToolMenuItem
              tool={tools.ToolCopy}
              controller={controller}
              selection={selection}
            />
            <ToolMenuItem
              tool={tools.ToolCopyImage}
              controller={controller}
              selection={selection}
            />
            <ToolMenuItem
              tool={tools.ToolCopyStyle}
              controller={controller}
              selection={selection}
            />
            <ToolMenuItem
              tool={tools.ToolPasteStyle}
              controller={controller}
              selection={selection}
            />
            <ToolMenuItem
              tool={tools.ToolCopyLink}
              controller={controller}
              selection={selection}
            >
              {selection?.type === 'activeSelection'
                ? 'Copy link to objects'
                : 'Copy link to object'}
            </ToolMenuItem>
            <ToolMenuItem
              tool={tools.ToolDuplicate}
              controller={controller}
              selection={selection}
            />
            <ToolMenuItem
              tool={tools.ToolDownloadImage}
              controller={controller}
              selection={selection}
            />
            <ToolMenuItem
              tool={tools.ToolEnableSmartZone}
              controller={controller}
              selection={selection}
            />
            <ToolMenuItem
              tool={tools.ToolDisableSmartZone}
              controller={controller}
              selection={selection}
            />
            <ToolMenuItem
              tool={tools.ToolExplodeSmartObject}
              controller={controller}
              selection={selection}
            />
            <ToolMenuItem
              tool={tools.ToolClearReactions}
              controller={controller}
              selection={selection}
            />
            <ToolMenuItem
              tool={tools.ToolUnlinkRecord}
              controller={controller}
              selection={selection}
            >
              Unlink{' '}
              {selection?.type === 'activeSelection' ? 'records' : 'record'}
            </ToolMenuItem>
            <ToolMenuItem
              tool={tools.ToolBulkEdit}
              controller={controller}
              selection={selection}
            />
            <ToolMenuItem
              tool={tools.ToolExportSelectionPNG}
              controller={controller}
              selection={selection}
            />
          </aha-menu-group>
          {customShapesMenuItem && (
            <aha-menu-group>{customShapesMenuItem}</aha-menu-group>
          )}
          {importRecordsMenuItem && importRecordMenu(controller)}
          <aha-menu-group>
            <ToolMenuItem
              tool={tools.ToolGroup}
              controller={controller}
              selection={selection}
            />
            <ToolMenuItem
              tool={tools.ToolUngroup}
              controller={controller}
              selection={selection}
            />
            <ToolMenuItem
              tool={tools.ToolBringToFront}
              controller={controller}
              selection={selection}
            />
            <ToolMenuItem
              tool={tools.ToolBringForward}
              controller={controller}
              selection={selection}
            />
            <ToolMenuItem
              tool={tools.ToolSendBackwards}
              controller={controller}
              selection={selection}
            />
            <ToolMenuItem
              tool={tools.ToolSendToBack}
              controller={controller}
              selection={selection}
            />
          </aha-menu-group>
        </>
      )}
      {availability.styleToolsOnly && (
        <aha-menu-group label='Actions'>
          <ToolMenuItem
            tool={tools.ToolCopyStyle}
            controller={controller}
            selection={selection}
          />
          <ToolMenuItem
            tool={tools.ToolPasteStyle}
            controller={controller}
            selection={selection}
          />
        </aha-menu-group>
      )}
      {availability.commenting && (
        <aha-menu-group label='Insert'>
          <ToolMenuItem
            tool={tools.ToolComment}
            controller={controller}
            selection={selection}
          />
        </aha-menu-group>
      )}
      {(availability.lockOrUnlock || availability.unlockAll) && (
        <aha-menu-group>
          {availability.lockOrUnlock && (
            <ToolMenuItem
              tool={selection?.isLocked ? tools.ToolUnlock : tools.ToolLock}
              controller={controller}
              selection={selection}
            />
          )}
          {availability.unlockAll && (
            <ToolMenuItem
              tool={tools.ToolUnlockAll}
              controller={controller}
              selection={selection}
            />
          )}
        </aha-menu-group>
      )}
      <ToolMenuItem
        tool={tools.ToolLayoutManual}
        controller={controller}
        selection={selection}
      />
      <ToolMenuItem
        tool={tools.ToolLayoutAutomatic}
        controller={controller}
        selection={selection}
      />
      {(tools.ToolLinkRecord.available({ controller, selection }) ||
        availability.createRecordsInfo) && (
        <aha-menu-group>
          {availability.createRecordsInfo && (
            <ConvertToMenu
              createRecordsInfo={availability.createRecordsInfo}
              controller={controller}
              selection={selection}
            />
          )}
          <ToolMenuItem
            tool={tools.ToolLinkRecord}
            controller={controller}
            selection={selection}
          />
        </aha-menu-group>
      )}
      {availability.deletion && (
        <aha-menu-group>
          <ToolMenuItem
            tool={tools.ToolDelete}
            controller={controller}
            selection={selection}
            type='danger'
            icon='fa-regular fa-trash-can'
          />
        </aha-menu-group>
      )}
    </>
  );
};

interface TableMenuItemsProps {
  controller: WhiteboardController;
  selection?: Table;
}

const TableMenuItems = ({ controller, selection }: TableMenuItemsProps) => {
  let multiRow = false;
  let multiColumn = false;
  const singleCellSelected = selection && hasSelectedTableCell(selection);
  const multipleCellsSelected = selection?.hasMultipleSelectedCells();
  if (multipleCellsSelected) {
    multiRow = selection.currentCell[0] > selection.previousCell[0];
    multiColumn = selection.currentCell[1] > selection.previousCell[1];
  }
  return (
    <>
      <aha-menu-group>
        <ToolMenuItem
          tool={tools.ToolAddRowAbove}
          controller={controller}
          selection={selection}
        />
        <ToolMenuItem
          tool={tools.ToolAddRowBelow}
          controller={controller}
          selection={selection}
        />
      </aha-menu-group>
      <aha-menu-group>
        <ToolMenuItem
          tool={tools.ToolAddColumnLeft}
          controller={controller}
          selection={selection}
        />
        <ToolMenuItem
          tool={tools.ToolAddColumnRight}
          controller={controller}
          selection={selection}
        />
      </aha-menu-group>
      <aha-menu-group>
        <ToolMenuItem
          tool={tools.ToolDeleteRows}
          controller={controller}
          selection={selection}
        >
          {multiRow ? 'Delete rows' : 'Delete row'}
        </ToolMenuItem>
        <ToolMenuItem
          tool={tools.ToolDeleteColumns}
          controller={controller}
          selection={selection}
        >
          {multiColumn ? 'Delete columns' : 'Delete column'}
        </ToolMenuItem>
        <ToolMenuItem
          tool={tools.ToolDeleteTable}
          controller={controller}
          selection={selection}
        />
      </aha-menu-group>
      {singleCellSelected && !multipleCellsSelected && (
        <aha-menu-group label='Actions'>
          <ToolMenuItem
            tool={tools.ToolCopyStyle}
            controller={controller}
            selection={selection}
          />
          <ToolMenuItem
            tool={tools.ToolPasteStyle}
            controller={controller}
            selection={selection}
          />
        </aha-menu-group>
      )}
      {selection?.mergeable && (
        <aha-menu-group>
          <ToolMenuItem
            tool={tools.ToolTableMergeCells}
            controller={controller}
            selection={selection}
          />
        </aha-menu-group>
      )}
      {tools.ToolComment.available({ controller, selection }) && (
        <aha-menu-group label='Insert'>
          <ToolMenuItem
            tool={tools.ToolComment}
            controller={controller}
            selection={selection}
          />
        </aha-menu-group>
      )}
    </>
  );
};

const MenuButton: ToolButtonComponent = ApplicationView(
  ({ selection, tool }) => {
    const controller = useController(WhiteboardController);
    const { renderKey, elementRef } = useElementRefresh(
      'aha-menu:open',
      undefined,
      true
    );

    return (
      <aha-menu ref={elementRef} style={{ minWidth: 40, minHeight: 40 }}>
        <ToolButton slot='control' tool={tool}>
          <aha-icon icon='fa-regular fa-ellipsis-h' />
        </ToolButton>
        <aha-menu-content>
          <ObjectMenuItems
            key={renderKey}
            controller={controller}
            selection={selection}
          />
        </aha-menu-content>
      </aha-menu>
    );
  }
);

class ToolMenu extends Tool {
  static toolName = 'More...';

  static available({ controller, selection }) {
    return Object.values(objectMenuAvailability(controller, selection)).some(
      Boolean
    );
  }

  static buttonComponent() {
    return MenuButton;
  }
}

export {
  ToolMenu as default,
  ToolMenuItem,
  CanvasMenuItems,
  ObjectMenuItems,
  TableMenuItems,
  CONTEXT_MENU_OBJECT,
  CONTEXT_MENU_CANVAS,
  CONTEXT_MENU_TABLE,
};
